/**
 * Protected Route
 * 
 * ป้องกัน Route ที่ต้อง login
 * - ถ้ายัง loading แสดง spinner
 * - ถ้าไม่ได้ login redirect ไป /login
 * - ถ้า login แล้วแสดง children
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth-provider';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  
  // Still checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">กำลังตรวจสอบสิทธิ์...</p>
        </div>
      </div>
    );
  }
  
  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Authenticated - render children
  return <>{children}</>;
}

/**
 * Guest Route (inverse of Protected)
 * 
 * สำหรับหน้า login - ถ้า login แล้วให้ redirect ไป dashboard
 */
interface GuestRouteProps {
  children: React.ReactNode;
}

export function GuestRoute({ children }: GuestRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  
  // Still checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">กำลังตรวจสอบ...</p>
        </div>
      </div>
    );
  }
  
  // Already authenticated - redirect to intended page or dashboard
  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }
  
  // Not authenticated - render login page
  return <>{children}</>;
}













